//
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { RequestRollToPaymentsPayload, StateTree } from '@makemydeal/dr-dash-types';
import { paymentPayloadUtils } from '@makemydeal/dr-offer-redux';
import type { GenericObject, OfferType, RollToOptionsOverrides } from '@makemydeal/dr-platform-types';
import { ApplicationCodes } from '@makemydeal/dr-platform-types';
import { TRACE_ID_HEADER } from '@makemydeal/dr-shared-bff-types';
import type { AnyAction, Dispatch } from 'redux';
import type { RSAASuccessType } from 'redux-api-middleware';
import { RSAA } from 'redux-api-middleware';
import { v4 as uuid } from 'uuid';
import { getUserProgramRollsUrl } from '../utils/paymentServiceUtils';
import { commitRollToEndpoint, ensureUserProgramQuotesForRollTo, getRollToSession } from '../utils/rollToEndpointUtils';
import {
    APPLY_ROLL_TO_OPTIONS,
    APPLY_ROLL_TO_OPTIONS_FAILURE,
    APPLY_ROLL_TO_OPTIONS_SUCCESS,
    REQUEST_ROLL_TO_PAYMENTS,
    REQUEST_ROLL_TO_PAYMENTS_FAILURE,
    REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR,
    RESET_ROLL_TO_OPTIONS,
    REVERT_ROLL_TO_ORIGINAL,
    ROLL_TO_PAYMENT_TOGGLE,
    SET_ROLL_TO_ACTIVE_TAB,
    SET_UNSAVED_CHANGES,
    UPDATE_ADJUST_ROLL_TO_CHANGES,
    UPDATE_ROLL_ADJUST,
    UPDATE_ROLL_TARGET_BALANCE_DUE,
    UPDATE_ROLL_TARGET_CASH_DUE,
    UPDATE_ROLL_TARGET_PAYMENT,
    UPDATE_ROLL_TARGET_PROFIT
} from '../actionTypes/rollToOptionsActionTypes';

// selectors
import type { ROLL_TO_OPTIONS, ROLL_TO_OPTIONS_LABELS } from '@makemydeal/dr-shared-types';
import * as tradeShopperActivity from '../shopperActivities/trade';
import * as tradeInsActionCreators from './tradeInsActionCreators';
import { buildPaymentPayloadHostSelectors } from '../utils/offerPayloadUtil';
import { getIncludedTradeInsWithOriginalIndexes } from '../selectors/tradeIns';
import * as rollToSelectors from '../selectors/rollToSelectors';
import { ROLL_TO_TARGET_FIELD_BY_OPTION, UPDATE_ROLL_TARGET_ACTION_TYPE_BY_OPTION } from '../constants';

export const getRollToPayment =
    ({ option }: { option: ROLL_TO_OPTIONS }) =>
    (targetValue: number) =>
    (dispatch: any, getState: any) => {
        const session = getRollToSession(getState());
        const { settled } = ensureUserProgramQuotesForRollTo({ dispatch, getState });

        const run = () => {
            const state = getState();

            const hostSelectors = buildPaymentPayloadHostSelectors();
            const payload = paymentPayloadUtils.createPaymentPayload(state, hostSelectors, {
                type: REQUEST_ROLL_TO_PAYMENTS,
                meta: {
                    isRollToCall: true,
                    sourceApplication: ApplicationCodes.MV
                }
            });

            const endpoint = getUserProgramRollsUrl(state);

            const rollToPayload = {
                ...payload,
                payment: {
                    ...payload.payment,
                    [option]: targetValue
                },
                traceId: uuid()
            };
            /* istanbul ignore next */
            const uprSuccessActionType: RSAASuccessType<StateTree, RequestRollToPaymentsPayload | undefined> = {
                type: REQUEST_ROLL_TO_PAYMENTS_SUCCESS_UPR,
                payload: async (_action, _state, res) => {
                    const data = await res.json();
                    return {
                        ...data,
                        [ROLL_TO_TARGET_FIELD_BY_OPTION[option]]: targetValue
                    };
                }
            };

            dispatch({ type: UPDATE_ROLL_TARGET_ACTION_TYPE_BY_OPTION[option], payload: targetValue });
            dispatch({
                [RSAA]: {
                    endpoint,
                    method: 'POST',
                    body: JSON.stringify(rollToPayload),
                    headers: { 'Content-Type': 'application/json', [TRACE_ID_HEADER]: rollToPayload.traceId ?? '' },
                    types: [REQUEST_ROLL_TO_PAYMENTS, uprSuccessActionType, REQUEST_ROLL_TO_PAYMENTS_FAILURE]
                }
            });
        };

        if (settled) {
            dispatch({ type: REQUEST_ROLL_TO_PAYMENTS });
            return settled.then(() => {
                if (getRollToSession(getState()) === session) run();
            });
        }

        return run();
    };

export const updateRollToAdjust = (newValue: any, meta: Record<'actionValueType', string>) => {
    return {
        type: UPDATE_ROLL_ADJUST,
        payload: newValue,
        meta
    };
};

export const updateAdjustRollToChanges = (newAdjustValues: any) => {
    return {
        type: UPDATE_ADJUST_ROLL_TO_CHANGES,
        payload: newAdjustValues
    };
};

export const updateRollTargetPayment = (newValue: number) => {
    return {
        type: UPDATE_ROLL_TARGET_PAYMENT,
        payload: newValue
    };
};

export const updateRollTargetBalanceDue = (newValue: number) => {
    return {
        type: UPDATE_ROLL_TARGET_BALANCE_DUE,
        payload: newValue
    };
};

export const updateRollTargetCashDue = (newValue: number) => {
    return {
        type: UPDATE_ROLL_TARGET_CASH_DUE,
        payload: newValue
    };
};

export const updateRollTargetProfit = (newValue: number) => {
    return {
        type: UPDATE_ROLL_TARGET_PROFIT,
        payload: newValue
    };
};

export const toggleRollToPayment = () => {
    return {
        type: ROLL_TO_PAYMENT_TOGGLE
    };
};

export const setUnsavedChanges = (hasUnsavedChanges: boolean) => ({
    type: SET_UNSAVED_CHANGES,
    payload: hasUnsavedChanges
});

export const revertRollToOriginal = (meta: Record<'actionValueType', string>) => {
    return {
        type: REVERT_ROLL_TO_ORIGINAL,
        meta
    };
};

export const applyRollToOptions = (payload: RollToOptionsOverrides, offerType: OfferType) => {
    const termValue = payload.options?.term?.value;

    return {
        type: APPLY_ROLL_TO_OPTIONS,
        meta: {
            isRollToCall: true,
            sourceApplication: ApplicationCodes.MV,
            middleware: {
                payment: true
            },
            ...(termValue && {
                paymentRequestOverride: {
                    [offerType]: {
                        termMonths: termValue
                    }
                }
            })
        },
        payload
    };
};

export const applyRollToOptionsSuccess =
    (action: AnyFSA) =>
    async (dispatch: Dispatch<AnyAction, StateTree>, getState: () => StateTree): Promise<void> => {
        const state = getState();
        const payload = action.meta.originalAction.payload || {};
        const trade = payload.options?.trade || {};
        const tradeIns = payload.options?.tradeIns; // Multiple trades
        const targetName = payload.target?.name;

        const currentValue = rollToSelectors.getCurrentValueByAdjustmentType(state, targetName);
        // tradeIn value(s)
        if (Array.isArray(tradeIns)) {
            // Multiple trades - dispatch allowanceChanged for each adjusted trade
            tradeIns.forEach((tradeItem: { value: number; adjusted: boolean; callerItemId: number }) => {
                if (
                    tradeItem.adjusted &&
                    tradeItem.callerItemId &&
                    tradeItem.callerItemId > 0 &&
                    tradeItem.callerItemId <= tradeIns.length
                ) {
                    // Use callerItemId - 1 as the index (callerItemId is 1-based, index is 0-based)
                    const index = tradeItem.callerItemId - 1;
                    const tradeInsWithOriginalIndexes = getIncludedTradeInsWithOriginalIndexes(state);
                    const originalIndex = tradeInsWithOriginalIndexes[index].originalIndex;
                    const action = tradeInsActionCreators.allowanceChanged(originalIndex);
                    dispatch(action(tradeItem.value, true));
                }
            });
        } else if (trade.adjusted) {
            dispatch(tradeShopperActivity.tradeActionCreators.allowanceChanged(trade.value, true));
        }
        commitRollToEndpoint(state);
        // close slideOut
        dispatch(toggleRollToPayment());
        dispatch({
            type: APPLY_ROLL_TO_OPTIONS_SUCCESS,
            payload: {
                name: targetName,
                current: currentValue,
                previous: payload.target?.value
            }
        });
    };

export const applyRollToOptionsError =
    (_action: AnyFSA, messages?: GenericObject[]) =>
    async (dispatch: Dispatch<AnyAction, StateTree>, _getState: () => StateTree): Promise<void> => {
        dispatch({
            type: APPLY_ROLL_TO_OPTIONS_FAILURE,
            payload: messages
        });
    };

export const resetRollToOptions = () => {
    return {
        type: RESET_ROLL_TO_OPTIONS
    };
};

export const setRollToActiveTab = (activeTab: ROLL_TO_OPTIONS_LABELS) => {
    return (dispatch: any, getState: any) => {
        const state = getState();

        // Determine if the target tab has a target value set
        const tabState = rollToSelectors.getTabState(state, activeTab);
        const targetValue =
            tabState.targetPayment ?? tabState.targetBalanceDue ?? tabState.targetCashDue ?? tabState.targetProfit ?? 0;
        const hasTargetValue = targetValue != null && targetValue > 0;

        // Only trigger UPR/UPQ calls if the tab has a target value
        dispatch({
            type: SET_ROLL_TO_ACTIVE_TAB,
            payload: activeTab,
            ...(hasTargetValue && {
                meta: {
                    middleware: {
                        rollTo: true
                    }
                }
            })
        });
    };
};
